import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Token } from 'vvs-sdk'
import { Flex, Text, Button, Modal, Heading } from 'vvs-uikit'
import BigNumber from 'bignumber.js'
import { useTranslation } from 'contexts/Localization'
import { PairState, usePair } from 'hooks/usePairs'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { getUserCraftsmansData } from 'hooks/useFarmGraph'
import { useLpTokenPrice } from 'state/farms/hooks'
import { showPoolImpermanentLoss } from 'utils'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { getBalanceAmount } from 'utils/formatBalance'
import { CurrencyLogo } from 'views/Info/components/CurrencyLogo'
import { TokenPairImage } from 'components/TokenImage'
import PairStats from './PoolPerformance/PairStats'
import LiquidityPool from './PoolPerformance/LiquidityPool'
import ImpermanentLoss from './PoolPerformance/ImpermanentLoss'

const Wrapper = styled(Flex)`
  svg {
    margin-right: 4px;
  }
`

const TabButton = styled(Button)<{ active: boolean }>`
  padding: 1px;
  margin-right: 32px;
  color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.text};
  border-bottom: ${({ active }) => active ? 'solid 3px' : 'none'};
  border-radius: 0;
  &:hover {
    border-bottom: solid 3px;
    color: ${({ theme }) => theme.colors.primary};
  }
`

interface PoolPerformanceModalModalProps {
  lpLabel?: string
  onDismiss?: () => void
  token: Token
  quoteToken: Token
  tokenPriceUsdc: string
  quoteTokenPriceUsdc: string
  pid: number
  stakedBalance: BigNumber
  pendingVVS: BigNumber
}

const PoolPerformanceModal: React.FC<PoolPerformanceModalModalProps> = ({
  onDismiss,
  lpLabel,
  token,
  quoteToken,
  tokenPriceUsdc,
  quoteTokenPriceUsdc,
  pid,
  stakedBalance,
  pendingVVS,
}) => {
  const { t } = useTranslation()

  const [activeIndex, setActiveIndex] = useState(1)
  const { account } = useActiveWeb3React()

  const token2quotePrice: BigNumber = new BigNumber(tokenPriceUsdc).div(quoteTokenPriceUsdc)
  const quote2tokenPrice: BigNumber = new BigNumber(quoteTokenPriceUsdc).div(tokenPriceUsdc)

  const uwToken = unwrappedToken(token)
  const uwQuoteToken = unwrappedToken(quoteToken)
  const [pairState, pair] = usePair(uwToken, uwQuoteToken)

  const lpTokenPrice: BigNumber = useLpTokenPrice(lpLabel)

  // get farm subgraph data
  const [token0Supplied, setToken0Supplied] = useState(null)
  const [token1Supplied, setToken1Supplied] = useState(null)
  const [avgToken0Price, setAvgToken0Price] = useState(null)
  const [avgToken1Price, setAvgToken1Price] = useState(null)
  const [totalHarvestedVVS, setTotalHarvestedVVS] = useState(null)

  useEffect(() => {
    const fetchCraftsmansData = async () => {
      const { data } = await getUserCraftsmansData(account, pid)
      if (
        data?.amount0Supplied &&
        data?.amount1Supplied &&
        data?.avgAmount0Price &&
        data?.avgAmount1Price &&
        data?.totalVVSClaimed
      ) {
        setToken0Supplied(data.amount0Supplied)
        setToken1Supplied(data.amount1Supplied)
        setAvgToken0Price(data.avgAmount0Price)
        setAvgToken1Price(data.avgAmount1Price)
        setTotalHarvestedVVS(data.totalVVSClaimed)
      }
    }
    if (account) {
      fetchCraftsmansData()
    }
  }, [account, pid])

  const renderContent = () => {
    switch (activeIndex) {
      case 1:
        return (pairState === PairState.EXISTS && pair
          ? <LiquidityPool
              lpLabel={lpLabel}
              lpTokenPrice={lpTokenPrice}
              stakedLPAmount={getBalanceAmount(stakedBalance)}
              pair={pair}
            />
          : null
      )
      case 2:
        return (token0Supplied && token1Supplied && pairState === PairState.EXISTS && pair
          ? <PairStats
              pair={pair}
              stakedLPAmount={stakedBalance}
              token0Supplied={new BigNumber(token0Supplied)}
              token1Supplied={new BigNumber(token1Supplied)}
            />
          : null
      )
      case 3:
        return (token0Supplied && token1Supplied && pairState === PairState.EXISTS && pair
          ? <ImpermanentLoss
              token={token}
              tokenPriceUsdc={tokenPriceUsdc}
              quoteTokenPriceUsdc={quoteTokenPriceUsdc}
              pair={pair}
              pendingVVS={getBalanceAmount(pendingVVS)}
              token0Supplied={getBalanceAmount(token0Supplied, pair.token0.decimals)}
              token1Supplied={getBalanceAmount(token1Supplied, pair.token1.decimals)}
              avgToken0Price={avgToken0Price}
              avgToken1Price={avgToken1Price}
              totalHarvestedVVS={getBalanceAmount(totalHarvestedVVS)}
              stakedLPAmount={getBalanceAmount(stakedBalance)}
              lpTokenPrice={lpTokenPrice}
            />
          : null
      )
      default:
        break
    }
    return null
  }

  return (
    <Modal title="" onDismiss={onDismiss} bodyPadding="4px 24px 24px 24px">
      {/* title */}
      <Wrapper justifyContent="start" alignItems="center" mb="12px">
        <TokenPairImage variant="inverted" primaryToken={token} secondaryToken={quoteToken} width={32} height={32} mr="20px"/>
        <Flex flexDirection="column" alignItems="flex-end">
          <Heading mb="4px">{lpLabel.split(' ')[0]}</Heading>
        </Flex>
      </Wrapper>

      {/* exchange rate */}
      <Flex flexDirection={['column', 'column', 'row']} mb={['8px', '8px', null]}>
          <Flex>
            <CurrencyLogo address={token.address} size="22px" />
            <Text fontSize="14px" ml="4px" style={{ whiteSpace: 'nowrap' }} width="fit-content">
              {`1 ${uwToken.symbol} = ${token2quotePrice.toFixed(6)} ${uwQuoteToken.symbol}`}
            </Text>
          </Flex>
          <Flex ml="10px">
            <CurrencyLogo address={quoteToken.address} size="22px" />
            <Text fontSize="14px" ml="4px" style={{ whiteSpace: 'nowrap' }} width="fit-content">
              {`1 ${uwQuoteToken.symbol} = ${quote2tokenPrice.toFixed(6)} ${uwToken.symbol}`}
            </Text>
          </Flex>
      </Flex>

      {/* menu */}
      <Flex mt="32px">
        <TabButton variant="text" scale="sm" active={activeIndex === 1} onClick={ () => { setActiveIndex(1) } }>
          {t('Overall Liquidity Info')}
        </TabButton>
        {(token0Supplied && token1Supplied && token0Supplied !== "0" && token1Supplied !== "0"
          ? <TabButton variant="text" scale="sm" active={activeIndex === 2} onClick={ () => { setActiveIndex(2) } }>
              {t('Pair Deposit Stats')}
            </TabButton>
          : null
        )}
        {(showPoolImpermanentLoss() && token0Supplied && token1Supplied && token0Supplied !== "0" && token1Supplied !== "0"
          ? <TabButton variant="text" scale="sm" active={activeIndex === 3} onClick={ () => { setActiveIndex(3) } }>
              {t('Impermanent Loss info')}
            </TabButton>
          : null
        )}
      </Flex>
      {renderContent()}

    </Modal>
  )
}

export default PoolPerformanceModal
